import type { PageMetaOverride } from '~/types/page-meta'

/**
 * Composable for accessing and applying page meta overrides
 *
 * Meta overrides are automatically fetched by the page-meta-checker middleware
 * and stored in the SSR context. This composable provides easy access to those
 * overrides for use in page components.
 *
 * @example
 * ```vue
 * <script setup>
 * const { metaOverride, applyMetaOverrides } = usePageMetaOverride()
 *
 * // Apply overrides to your meta
 * const headData = computed(() => ({
 *   title: metaOverride.value?.title || 'Default Title',
 *   meta: [
 *     {
 *       hid: 'description',
 *       name: 'description',
 *       content: metaOverride.value?.description || 'Default description',
 *     },
 *   ],
 * }))
 *
 * useHead(headData)
 * ```
 */
export const usePageMetaOverride = () => {
  const nuxtApp = useNuxtApp()

  /**
   * Get the meta override from the SSR context
   * Returns null if no override exists for this page
   */
  const metaOverride = computed<PageMetaOverride | null>(() => {
    if (process.server) {
      // Server-side: get from event context (set by middleware)
      const event = nuxtApp.ssrContext?.event
      return event?.context?.pageMetaOverride || null
    } else {
      // Client-side: get from payload (hydrated from server)
      return nuxtApp.payload?.pageMetaOverride || null
    }
  })

  /**
   * Helper function to apply meta overrides to existing meta data
   *
   * Takes your default meta values and applies any overrides that exist.
   * Only non-null override values will replace defaults.
   *
   * @param defaultTitle - Default page title
   * @param defaultDescription - Default meta description
   * @param defaultH1 - Default H1 text (optional, for reference only)
   * @returns Object with title, description, and h1 (all potentially overridden)
   *
   * @example
   * ```vue
   * const meta = applyMetaOverrides(
   *   'Default Title | Site Name',
   *   'Default description text',
   *   'Default H1 Heading'
   * )
   *
   * useHead({
   *   title: meta.title,
   *   meta: [
   *     { hid: 'description', name: 'description', content: meta.description }
   *   ]
   * })
   * ```
   */
  const applyMetaOverrides = (
    defaultTitle: string,
    defaultDescription: string,
    defaultH1?: string,
  ) => {
    const override = metaOverride.value

    return {
      title: override?.title || defaultTitle,
      description: override?.description || defaultDescription,
      h1: override?.h1 || defaultH1 || null,
    }
  }

  /**
   * Check if any meta overrides exist for this page
   */
  const hasOverride = computed(() => {
    return metaOverride.value !== null
  })

  return {
    /**
     * The meta override object (or null if none exists)
     */
    metaOverride,

    /**
     * Helper function to apply overrides to default values
     */
    applyMetaOverrides,

    /**
     * Boolean indicating if any override exists
     */
    hasOverride,
  }
}
